"use client"

import type React from "react"
import { ArrowLeft, AlertTriangle, RefreshCw } from "lucide-react"
import { useEffect, useState } from "react"

interface ErrorPageProps {
  errorCode?: number
  onRetry?: () => void
}

const ErrorPage: React.FC<ErrorPageProps> = ({ errorCode = 404, onRetry }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [floatingCoins, setFloatingCoins] = useState<
    Array<{ id: number; x: number; y: number; size: number; speed: number; rotation: number }>
  >([])

  useEffect(() => {
    setIsVisible(true)

    const coins = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 30 + 20,
      speed: Math.random() * 15 + 5,
      rotation: Math.random() * 360,
    }))
    setFloatingCoins(coins)
  }, [])

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">


      {/* Network lines */}
      <div className="absolute inset-0 opacity-20">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="networkPattern" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M10,10 L90,90 M90,10 L10,90 M50,0 L50,100 M0,50 L100,50" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#networkPattern)" />
        </svg>
      </div>

     
      {/* Main content */}
      <div
        className={`relative z-10 max-w-2xl w-full mx-4 transition-all duration-700 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
      >
        {/* Error box */}
        <div className="bg-blue-950/80 backdrop-blur-md rounded-lg border-2 border-yellow-500 shadow-2xl shadow-blue-500/20 overflow-hidden">
          {/* Header */}
          

          {/* Content */}
          <div className="p-6 md:p-8 flex flex-col items-center">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full blur-md animate-pulse"></div>
                <AlertTriangle className="h-16 w-16 text-yellow-500 relative z-10" />
              </div>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-center text-yellow-500 mb-2">
              Oops! Something went wrong
            </h3>

          

            {/* Button */}
            <button
              onClick={onRetry}
              className="group relative w-full sm:w-auto flex justify-center items-center rounded border-2 border-yellow-600 bg-gradient-to-r from-yellow-600 to-orange-500 py-3 px-8 text-base font-medium text-white hover:from-yellow-500 hover:to-orange-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <RefreshCw className="h-5 w-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
              Try Again
            </button>
          </div>
        </div>

        {/* Bottom button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-yellow-300 hover:text-yellow-100 transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Go Back to Home
          </button>
        </div>
      </div>
     
    </div>
  )
}

// Add keyframes for animations
const styles = `
@keyframes twinkle {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-20px);
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
`

// Add the styles to the document
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style")
  styleElement.textContent = styles
  document.head.appendChild(styleElement)
}

export default ErrorPage

